import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { ArrowRight, Quote, Play, Star } from "lucide-react";
import CompanyLayout from "@/components/CompanyLayout";
import { DemoBookingModal } from "@/components/DemoBookingModal";
import { SupademoModal } from "@/components/SupademoModal";
import companyLogo from "@/assets/case-studies/mmlc-logo.webp";
import heroImage from "@/assets/case-studies/mmlc-hero.jpg";
import jessicaPhoto from "@/assets/case-studies/jessica-redmond.jpeg";
import { usePostHog } from "@/hooks/usePostHog";

// Author info
const AUTHOR = {
  name: "Jessica Redmond",
  title: "Preschool Teacher",
  company: "Minding Miracles Learning Center",
  photo: jessicaPhoto
};

// Reusable Testimonial Component
interface TestimonialProps {
  quote: string;
  variant?: 'blue' | 'purple' | 'white';
  size?: 'default' | 'large';
  showAuthor?: boolean;
}

const Testimonial: React.FC<TestimonialProps> = ({ 
  quote, 
  variant = 'blue', 
  size = 'default',
  showAuthor = false 
}) => {
  const variantStyles = {
    blue: "bg-blue-50 border-l-4 border-blue-600",
    purple: "bg-blue-50 border-l-4 border-blue-600",
    white: "bg-gray-50 border-l-4 border-gray-300"
  };

  const quoteColor = 'text-blue-600';
  const textSize = size === 'large' ? 'text-xl' : 'text-lg';

  return (
    <div className={`${variantStyles[variant]} p-6 my-8 rounded-lg`}>
      <Quote className={`h-8 w-8 ${quoteColor} mb-4`} />
      <blockquote className={`${textSize} text-gray-800 italic ${showAuthor ? 'mb-4' : ''}`}>
        {quote}
      </blockquote>
    </div>
  );
};

const MMLCCaseStudy = () => {
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [isSupademoModalOpen, setIsSupademoModalOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('background');
  const { posthog } = usePostHog();

  // Track active section on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.3, rootMargin: '-100px 0px -60% 0px' }
    );

    const sections = document.querySelectorAll('[data-section]');
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const top = element.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  const sections = [
    { id: 'background', label: 'Background' },
    { id: 'discovery', label: 'Discovery' },
    { id: 'implementation', label: 'Implementation' },
    { id: 'results', label: 'Results' },
    { id: 'customer-experience', label: 'Customer Experience' },
    { id: 'success-story', label: 'Success Story' },
    { id: 'looking-ahead', label: 'Looking Ahead' }
  ];

  return (
    <>
      <DemoBookingModal 
        open={isDemoModalOpen} 
        onOpenChange={setIsDemoModalOpen} 
      />
      <SupademoModal 
        open={isSupademoModalOpen} 
        onOpenChange={setIsSupademoModalOpen} 
      />
      <CompanyLayout
        title="Minding Miracles Case Study: Building Culture Across Multiple Centers"
        description="Discover how Minding Miracles Learning Center unified recognition across three preschools and an Adult Center using Assembly."
      >
        <div className="bg-white">
          {/* Hero Section */}
          <section className="relative bg-gray-50 pt-24 pb-16 border-b border-gray-200">
            <div className="container mx-auto px-4 2xl:px-12 max-w-none lg:max-w-7xl xl:max-w-[1400px] 2xl:max-w-[1600px]">
              <div className="flex flex-col lg:grid lg:grid-cols-7 gap-8 lg:gap-12 items-center">
                <div className="lg:col-span-4 flex flex-col justify-center">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="inline-flex items-center bg-white px-4 py-2 rounded-full border border-gray-200">
                      <span className="text-sm font-medium text-gray-600">Case Study</span>
                    </div>
                    <img 
                      src={companyLogo} 
                      alt="Minding Miracles Learning Center" 
                      className="h-8 object-contain"
                    />
                  </div>
                  <h1 className="text-3xl sm:text-4xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                    How Minding Miracles Learning Center Brought Every Building Together with Assembly
                  </h1>
                  <p className="text-lg sm:text-xl text-gray-600 mb-8 leading-relaxed">
                    Building bonds beyond classrooms with unified recognition across three preschools and an Adult Center dedicated to inclusion and supporting children with Autism.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 mb-6">
                    <Button 
                      size="lg" 
                      className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white" 
                      onClick={() => setIsDemoModalOpen(true)}
                    >
                      Book a Demo
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="lg" 
                      className="w-full sm:w-auto border-gray-300 text-gray-700 hover:bg-gray-50 hover:text-gray-900 hover:border-gray-400" 
                      onClick={() => {
                        posthog?.capture('see_it_in_action_clicked', {
                          page: '/resources/case-studies/mmlc',
                          section: 'hero',
                          button_text: 'See it in Action',
                          url: window.location.href,
                          opens_modal: true
                        });
                        setIsSupademoModalOpen(true);
                      }}
                    >
                      See it in Action
                      <Play className="w-4 h-4 ml-2" fill="currentColor" />
                    </Button>
                  </div>
                  
                  {/* G2 Rating Badge */}
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-6 items-start sm:items-center text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                      <span className="font-medium">4.9 / 5 on G2</span>
                      <img src="/lovable-uploads/e68d106f-69a9-45e8-8624-7f09b1ae1e6f.png" alt="G2 Logo" className="h-4 w-4" />
                    </div>
                    <a href="https://www.g2.com/products/assembly-2021-12-09/reviews" target="_blank" rel="noopener noreferrer" className="hover:text-blue-600 transition-colors">
                      3000+ reviews
                    </a>
                  </div>
                </div>
                <div className="lg:col-span-3 relative w-full">
                  <div className="aspect-[4/3] rounded-xl overflow-hidden border border-gray-200">
                    <img 
                      src={heroImage} 
                      alt="Happy children learning together" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Main Content with Sidebar Layout */}
          <div className="bg-white">
            <div className="container mx-auto px-4 2xl:px-12 max-w-none lg:max-w-7xl xl:max-w-[1400px] 2xl:max-w-[1600px]">
              <div className="flex flex-col lg:flex-row gap-8 py-12">
                
                {/* Sticky Sidebar */}
                <aside className="lg:w-80 flex-shrink-0 mx-auto lg:mx-0 max-w-sm lg:max-w-none">
                  <div className="lg:sticky lg:top-24">
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                      
                      {/* Reviewer Section */}
                      <div className="p-6 text-center lg:text-left">
                        <img 
                          src={AUTHOR.photo} 
                          alt={AUTHOR.name} 
                          className="w-20 h-20 rounded-full object-cover mb-4 border-4 border-blue-100 mx-auto lg:mx-0"
                        />
                        <h3 className="font-bold text-gray-900 text-lg mb-1">{AUTHOR.name}</h3>
                        <p className="text-sm text-gray-600 mb-4">{AUTHOR.title}</p>
                        <img 
                          src={companyLogo} 
                          alt="Minding Miracles" 
                          className="h-10 object-contain mx-auto lg:mx-0"
                        />
                      </div>

                      {/* Company Details */}
                      <div className="px-6 py-4 border-t border-gray-100 space-y-4 text-center lg:text-left">
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Industry</p>
                          <p className="text-sm font-medium text-gray-900">Early Childhood Education & Inclusion</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Company Size</p>
                          <p className="text-sm font-medium text-gray-900">Small Business</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Locations</p>
                          <p className="text-sm font-medium text-gray-900">3 Preschools + Adult Center</p>
                        </div>
                      </div>

                      {/* Section Navigation - Hidden on mobile/tablet */}
                      <nav className="hidden lg:block px-6 py-4 border-t border-gray-100">
                        <h3 className="font-bold text-gray-900 mb-4 text-sm uppercase tracking-wide">Jump To</h3>
                        <ul className="space-y-2">
                          {sections.map((section) => (
                            <li key={section.id}>
                              <button
                                onClick={() => scrollToSection(section.id)}
                                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                                  activeSection === section.id
                                    ? 'bg-blue-100 text-blue-900 font-semibold'
                                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                }`}
                              >
                                {section.label}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </nav>
                    </div>
                  </div>
                </aside>

                {/* Main Content */}
                <main className="flex-1 min-w-0">
                  {/* Results Highlight Card */}
                  <div className="bg-blue-600 rounded-xl p-8 mb-12 text-white border border-blue-700">
                    <div className="grid md:grid-cols-2 gap-8">
                      <div>
                        <div className="text-4xl font-bold mb-2">Company-wide</div>
                        <p className="text-blue-100">Increased participation across all centers</p>
                      </div>
                      <div>
                        <div className="text-4xl font-bold mb-2">Higher Morale</div>
                        <p className="text-blue-100">Stronger teamwork and idea-sharing</p>
                      </div>
                    </div>
                  </div>

                  {/* Background Section */}
                  <section id="background" data-section className="mb-16">
                    <div>
                      <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                        Background: Building Bonds Beyond Classrooms
                      </h2>
                      <div className="prose prose-lg max-w-none">
                        <p className="text-gray-700 leading-relaxed mb-6">
                          At Minding Miracles Learning Center, every day begins with empathy. With three preschools and an Adult Center dedicated to inclusion and supporting children with Autism, the team lives by a simple mission: every person matters.
                        </p>
                        
                        <p className="text-gray-700 leading-relaxed mb-6">
                          The same care that guided the classrooms inspired the team to look inward, to build even stronger bonds among staff across all locations.
                        </p>

                        <Testimonial 
                          quote="We wanted more camaraderie between team members, not just in one building, but across all of them. Recognition was happening - but it wasn't unified or visible to everyone."
                        />

                        <p className="text-gray-700 leading-relaxed">
                          Before Assembly, appreciation came through raffles, gift cards, and occasional messages from managers. Thoughtful, yes, but scattered. Each building celebrated differently, leaving some moments unnoticed and others forgotten. What the team longed for was a shared space where everyone could see, celebrate, and support one another.
                        </p>
                      </div>
                    </div>
                  </section>

                  {/* Discovery Section */}
                  <section id="discovery" data-section className="mb-16">
                    <div>
                      <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                        Discovering an Employee Recognition Platform That Felt Personal
                      </h2>
                      <div className="prose prose-lg max-w-none">
                        <p className="text-gray-700 leading-relaxed mb-6">
                          The search for a better system began simply, with a Google search for best employee rewards and recognition platforms.
                        </p>
                        
                        <p className="text-gray-700 leading-relaxed mb-6">
                          That's how Minding Miracles found Assembly.
                        </p>

                        <Testimonial 
                          quote="We had a Zoom call with the founder himself. That personal touch made a big impression."
                          variant="white"
                        />

                        <p className="text-gray-700 leading-relaxed">
                          From that moment, Assembly stood out for its warmth, inclusivity, and thoughtful design. It allowed every staff member, not just administrators, to recognize peers, celebrate milestones, and share stories that connected classrooms and centers.
                        </p>
                      </div>
                    </div>
                  </section>

                  {/* Implementation Section */}
                  <section id="implementation" data-section className="mb-16">
                    <div>
                      <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                        Implementation: Turning Engagement into a Habit
                      </h2>
                      <div className="prose prose-lg max-w-none">
                        <Testimonial 
                          quote="We loved that appreciation didn't have to mean money. It could be as simple as a shout-out that everyone sees."
                        />

                        <p className="text-gray-700 leading-relaxed">
                          Features like automated birthdays and anniversaries, forums for sharing ideas, and challenges that spark teamwork made Assembly an easy fit. It wasn't just software, it felt like a digital extension of the culture they were trying to build.
                        </p>
                      </div>
                    </div>
                  </section>

                  {/* Results Section */}
                  <section id="results" data-section className="mb-16">
                    <div>
                      <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                        Results: A Culture of Everyday Appreciation
                      </h2>
                      
                      <div className="prose prose-lg max-w-none">
                        <p className="text-gray-700 leading-relaxed mb-6">
                          When Minding Miracles launched Assembly in September, participation was modest at first. But as the holidays approached, contests and classroom challenges began to light up the platform.
                        </p>

                        <Testimonial 
                          quote="Once we rolled out our company-wide contests, everything changed. You could feel the energy build."
                          variant="white"
                        />

                        <p className="text-gray-700 leading-relaxed mb-6">
                          Teachers began cheering for one another, sharing classroom ideas, and helping peers replicate great activities from other centers. The recognition feed soon became a live stream of positivity, proof that kindness spreads when it's visible.
                        </p>

                        <p className="text-gray-700 leading-relaxed mb-6">
                          Automated birthday and anniversary reminders also lifted a burden off managers. Staff no longer had to track dates or coordinate celebrations, the system remembered, so no one felt forgotten.
                        </p>

                        <Testimonial 
                          quote="The participation and morale have both improved so much. It's made a real difference in how connected we feel."
                          showAuthor={true}
                          size="large"
                        />
                      </div>
                    </div>
                  </section>

                  {/* Customer Experience Section */}
                  <section id="customer-experience" data-section className="mb-16">
                    <div>
                      <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                        Customer Experience: Partnership that Feels Personal
                      </h2>
                      
                      <div className="prose prose-lg max-w-none">
                        <p className="text-gray-700 leading-relaxed mb-6">
                          Beyond the platform itself, what sets Assembly apart is the support behind it. From the very first Zoom call with the founder to quick responses from the customer success team, Minding Miracles felt like they were working with a partner, not a vendor.
                        </p>

                        <Testimonial 
                          quote="Everything we've asked for has been answered, and often solved. The support has been so amazing."
                        />

                        <p className="text-gray-700 leading-relaxed">
                          This responsive partnership has made implementation smoother and problem-solving faster, allowing the team to focus on what matters most: the children and their staff.
                        </p>
                      </div>
                    </div>
                  </section>

                  {/* Success Story Section */}
                  <section id="success-story" data-section className="mb-16">
                    <div>
                      <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                        Success Story: The 12 Days of Christmas Assembly Challenge
                      </h2>
                      
                      <div className="prose prose-lg max-w-none">
                        <p className="text-gray-700 leading-relaxed mb-6">
                          The most transformative experience came during the "12 Days of Christmas Assembly Challenge."
                        </p>

                        <p className="text-gray-700 leading-relaxed mb-6">
                          The CEO designed a playful series of daily tasks, from cookie exchanges and toy drives to team-based classroom activities, with staff posting photo proof of their participation.
                        </p>

                        <Testimonial 
                          quote="It was incredible. Everyone got involved, and the friendly competition brought so much joy."
                          variant="white"
                        />

                        <p className="text-gray-700 leading-relaxed mb-6">
                          Each day ran on its own flow, opening at midnight, closing before the next began. Participants earned diamonds for each completed challenge, and those who finished all twelve earned bonus recognition.
                        </p>

                        <p className="text-gray-700 leading-relaxed">
                          The challenge sparked a company-wide wave of collaboration, creativity, and laughter.
                        </p>
                      </div>
                    </div>
                  </section>

                  {/* Looking Ahead Section */}
                  <section id="looking-ahead" data-section className="mb-16">
                    <div>
                      <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                        Looking Ahead: A Culture That Lasts
                      </h2>
                      
                      <div className="prose prose-lg max-w-none">
                        <p className="text-gray-700 leading-relaxed mb-6">
                          Today, Assembly is woven into the daily rhythm of Minding Miracles Learning Center. What started as a desire for better recognition has grown into a vibrant culture of appreciation, one where everyone, from classroom aides to center directors, feels seen and celebrated.
                        </p>

                        <p className="text-gray-700 leading-relaxed mb-6">
                          The platform has become more than a tool - it's the digital heartbeat of a mission-driven organization.
                        </p>

                        <Testimonial 
                          quote="Assembly has truly been one of the best decisions we've made. It's helped us build the kind of workplace we always wanted, and that's something worth celebrating."
                        />

                        <p className="text-gray-700 leading-relaxed">
                          For organizations like Minding Miracles, where compassion is the foundation and connection is the goal, Assembly has proven to be the perfect partner in building a culture that truly cares.
                        </p>
                      </div>
                    </div>
                  </section>

                </main>
              </div>
            </div>
          </div>

        {/* CTA Footer Section */}
        <section className="py-20 bg-blue-600 border-t border-blue-700">
          <div className="container mx-auto px-4 max-w-5xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Smooth starts lead to stronger partnerships - ready to begin yours?
            </h2>
            <div className="mt-10">
              <Button 
                size="lg" 
                className="bg-white text-blue-600 hover:bg-gray-100"
                onClick={() => setIsDemoModalOpen(true)}
              >
                Book a Demo
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </section>
        </div>
      </CompanyLayout>
    </>
  );
};

export default MMLCCaseStudy;
